import React, { useState } from 'react';
import { KeyRound } from 'lucide-react';
import { ConfirmModal } from './ui/Modal';

/**
 * The "Include credentials" switch for snapshot shares, and the copy that goes
 * with it.
 *
 * One component for BOTH share surfaces (`ShareToChatModal` and the composer's
 * `ShareDrilldownPicker`) on purpose: divergent copy across two surfaces is how
 * a security contract rots, and the alternative considered — leaving the picker
 * silently safe with no toggle — protects the user who does not know while
 * stranding the user who does. A teammate deliberately sending a working
 * collection would get a broken one with no explanation and no control, and the
 * predictable next move is pasting the token into the chat body instead, which
 * is worse.
 *
 * Off by default, deliberately. The snapshot is handed to everyone in the
 * channel, so sending working credentials has to be a choice somebody made.
 *
 * Encryption (`utils/shareEnvelope.js`) did NOT retire this switch and must not
 * be allowed to look like it did. The share is encrypted under the CHANNEL key,
 * which our server mints and hands to any current member — so encryption
 * changes what our database holds, not who in the conversation can read the
 * share. Redaction is the only thing that decides whether a recipient gets a
 * working credential.
 *
 * The copy is written to be HONEST rather than reassuring — "honest labelling
 * beats fake enforcement". "Credentials are removed" reads as a completeness
 * claim, and it is not one: request bodies and scripts are sent as typed,
 * because a best-effort rewrite of a script produces a script that runs and
 * quietly does the wrong thing on the recipient's machine.
 */

/**
 * One line each, and the OFF line carries the limit.
 *
 * Two constraints, both real, and the first version of this satisfied only one
 * at a time. Explaining the whole redaction contract inline ran to three lines
 * in a modal that also has a preview list and a conversation picker, and pushed
 * the picker off-screen. Moving the caveat into the confirmation fixed the
 * layout and broke the honesty: the confirmation is reachable ONLY by turning
 * the toggle ON, so the person who takes the safe default — the common path,
 * the one this feature exists for — was told "Credentials are removed" with no
 * qualification at all.
 *
 * That is a completeness claim and the implementation cannot meet it: bodies,
 * both scripts, collection documentation, and any header, param or variable
 * that neither the name lists nor `looksSecret` recognise all still travel.
 * "Honest labelling beats fake enforcement" is the standing rule.
 *
 * So: the limit goes in the OFF state where it applies, in one clause. The
 * fuller explanation stays in the confirmation, where somebody is deciding.
 */
export const OFF_COPY = 'Credentials are removed — bodies and scripts are sent as-is.';

export const ON_COPY = 'Working credentials will be sent.';

/**
 * Turning the switch ON gets a confirmation, not just a switch — this is a
 * disclosure the backend facts justify, not scaremongering.
 *
 * `ConfirmModal` and not `window.confirm`: `dialog:default` has no
 * `allow-confirm`, so `window.confirm` THROWS in the packaged app. jsdom would
 * never catch that.
 */
// One flowing paragraph: `ui/Modal.js` renders this into a <p> with no
// `white-space: pre-line`, so the `\n\n` this used to contain collapsed into a
// run-on block anyway (review N8).
// Two clauses changed when the work behind them shipped, and only those two.
// "stored on our servers in readable form" became false when the payload became
// an envelope; "removing the message does not delete it" became false when
// `Chat.soft_delete_message/2` started hard-deleting the share row inside the
// same transaction. Everything about the AUDIENCE is unchanged and still true
// word for word — the channel key is server-minted and handed to any current
// member, so a person added tomorrow can open a share sent yesterday.
//
// "Encrypted before it leaves this device and stored that way" is the strongest
// sentence that is still true. It deliberately does not say end-to-end, does
// not say only-the-recipient, and does not imply Apiboo cannot read it; all
// three would be false, and `utils/chatCrypto.js` already refuses to say them
// about attachments for exactly this reason.
export const CONFIRM_MESSAGE =
  'Auth, credential headers, cookies and secret variable values will be sent as they are, to ' +
  'everyone in the conversation — including anyone added to the conversation later. The ' +
  'snapshot is encrypted before it leaves this device and stored that way, like the rest of ' +
  'your data, and it does not expire. Removing the message deletes it; anything a recipient ' +
  'already saved stays theirs. Request bodies and scripts are sent as-is either way: a ' +
  'credential typed directly into one is shared whichever way this switch is set.';

export default function ShareCredentialsToggle({
  includeCredentials,
  onChange,
  risks = [],
  labelId = 'share-creds-label',
}) {
  const [confirming, setConfirming] = useState(false);

  const toggle = () => {
    // Turning it OFF is always safe and immediate; turning it ON is the
    // decision that needs to be made explicitly.
    if (includeCredentials) onChange(false);
    else setConfirming(true);
  };

  return (
    <div className="rounded-lg border border-gray-200 dark:border-zinc-700 px-3 py-2">
      <div className="settings-row" style={{ borderBottom: 'none', padding: 0 }}>
        <span
          className="modal-toggle-label"
          style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.875rem' }}
          id={labelId}
        >
          <KeyRound size={13} style={{ flexShrink: 0 }} className="modal-toggle-icon" />
          Include credentials
        </span>
        <button
          type="button"
          className={`settings-toggle${includeCredentials ? ' settings-toggle--on' : ''}`}
          onClick={toggle}
          aria-checked={includeCredentials}
          aria-labelledby={labelId}
          role="switch"
        >
          <span className="settings-toggle-thumb" />
        </button>
      </div>

      <div className="text-xs text-gray-400 dark:text-zinc-500" style={{ marginTop: 4 }}>
        {includeCredentials ? ON_COPY : OFF_COPY}
      </div>

      {/* Named, not rewritten: the precedent is `findExposedSecretKeys`, which
          exists to TELL the user what is exposed and leave the decision to
          them. Kept to ONE line here — the full sentence is in the confirm. */}
      {risks.length > 0 && (
        <div
          className="text-xs text-amber-600 dark:text-amber-400"
          style={{
            marginTop: 4,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={`${risks.map((r) => r.requestName).join(', ')} may have credentials in their body or scripts, which cannot be removed automatically.`}
        >
          Possible secrets in {risks.length} request{risks.length === 1 ? '' : 's'}' body or scripts
        </div>
      )}

      <ConfirmModal
        isOpen={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={() => onChange(true)}
        title="Send working credentials?"
        message={CONFIRM_MESSAGE}
        confirmText="Send credentials"
        type="warning"
      />
    </div>
  );
}
